// src/Pages/UploadData.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Button, Paper, IconButton } from '@mui/material';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from '../Components/Snackbar/SnackbarContext';
import * as XLSX from 'xlsx';

import apiEndpoints from '../apiconfig';

const UploadData = () => {
  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [files, setFiles] = useState([]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setFiles(prevFiles => [...prevFiles, ...Array.from(e.target.files)]);
    }
  };

  const handleRemoveFile = (indexToRemove) => {
    setFiles(prevFiles => prevFiles.filter((_, idx) => idx !== indexToRemove));
  };

  const cleanContact = (contactStr) => {
    if (!contactStr || contactStr === "-") return "-";
    let cleaned = String(contactStr).toLowerCase().replace('p:', '').trim();
    let digitsOnly = cleaned.replace(/\D/g, '');
    if (digitsOnly.length === 10) return '+91' + digitsOnly;
    if (digitsOnly.length === 12 && digitsOnly.startsWith('91')) return '+' + digitsOnly;
    if (digitsOnly.length === 11 && digitsOnly.startsWith('0')) return '+91' + digitsOnly.substring(1);
    return cleaned || "-";
  };

  const extractStandardColumns = (jsonArray, fileName) => {
    if (jsonArray.length === 0) return [];
    return jsonArray.map(row => {
      const keys = Object.keys(row);
      const getVal = (possibleNames) => {
        for (let p of possibleNames) {
          const exactMatch = keys.find(k => k.toLowerCase() === p);
          if (exactMatch && row[exactMatch]) return row[exactMatch];
        }
        for (let p of possibleNames) {
          const partialMatch = keys.find(k => k.toLowerCase().includes(p) && !['ad_', 'campaign_', 'adset_'].some(ex => k.toLowerCase().includes(ex)));
          if (partialMatch && row[partialMatch]) return row[partialMatch];
        }
        return "-";
      };
      return {
        "Full Name": getVal(['full_name', 'name', 'first_name', 'full name']),
        "Contact": cleanContact(getVal(['phone_number', 'phone', 'contact', 'mobile', 'number'])),
        "Mail ID": getVal(['email', 'mail', 'mail id']),
        "Address": getVal(['street_address', 'address', 'city', 'location', 'region', 'street', 'zip']),
        "sourceFile": fileName
      };
    }).filter(row => {
      if (Object.values(row).every(val => !val || val === "-" || val.trim() === "")) return false;
      if (/test lead|dummy data/i.test(row["Full Name"])) return false;
      return true;
    });
  };

  const readFileAsJSON = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const data = e.target.result;
          const workbook = XLSX.read(data, { type: 'binary' });
          const sheetName = workbook.SheetNames[0];
          const json = XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "" });
          resolve(extractStandardColumns(json, file.name));
        } catch (err) { reject(err); }
      };
      reader.onerror = reject;
      reader.readAsBinaryString(file);
    });
  };

  const removeDuplicates = (dataArray) => {
    const uniqueMap = new Map();
    dataArray.forEach(row => {
      const name = (row["Full Name"] || "").toString().trim().toLowerCase();
      const contact = (row["Contact"] || "").toString().trim().toLowerCase();
      const email = (row["Mail ID"] || "").toString().trim().toLowerCase();
      const address = (row["Address"] || "").toString().trim().toLowerCase();
      
      // Create a composite key for true uniqueness based on user requirements
      const key = `${name}|${contact}|${email}|${address}`;
      
      if (!uniqueMap.has(key)) {
        uniqueMap.set(key, row);
      }
    });
    return Array.from(uniqueMap.values());
  };

  const handleUpload = async () => {
    if (files.length === 0) {
      showSnackbar('Please select at least one file', 'error');
      return;
    }

    try {
      // 1. Upload & Read Data
      const promises = files.map(file => readFileAsJSON(file));
      const newMappedDataArray = await Promise.all(promises);

      // 2. We can still do client-side merging/deduping before sending or just send all
      // The user wants it in DB. Let's merge it with what's in DB eventually, 
      // but for now let's dedupe the NEW batch.
      const flattened = newMappedDataArray.flat();
      const uniqueNewData = removeDuplicates(flattened);

      // 3. Send to DB
      const response = await fetch(apiEndpoints.sheet_data, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          action: 'bulk_upload',
          data: uniqueNewData
        })
      });
      const result = await response.json();

      if (result.status === 'success') {
        showSnackbar(result.message, 'success');
        navigate('/admin/view-data');
      } else {
        showSnackbar(result.message || 'Error uploading data', 'error');
      }
    } catch (error) {
      console.error(error);
      showSnackbar('Error processing files', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: '#1E293B', mb: 1 }}>
          Upload File
        </Typography>
        <Typography variant="body2" sx={{ color: '#64748B' }}>
          Please upload multiple Excel (.xlsx) or CSV sheets to automatically parse, merge, and clean the data.
        </Typography>
      </Box>

      <Paper
        sx={{
          p: 6,
          borderRadius: 3,
          bgcolor: 'rgba(255, 255, 255, 0.4)',
          backdropFilter: 'blur(10px)',
          border: '2px dashed rgba(99, 102, 241, 0.4)',
          boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.05)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 3
        }}
      >
        <CloudUploadIcon sx={{ fontSize: 60, color: '#818CF8' }} />

        <Box sx={{ textAlign: 'center' }}>
          <Button
            variant="contained"
            component="label"
            sx={{
              bgcolor: '#4F46E5',
              textTransform: 'none',
              borderRadius: 2,
              px: 4,
              py: 1.5,
              fontWeight: 600,
              '&:hover': { bgcolor: '#4338CA' }
            }}
          >
            Select Sheet Files
            <input
              type="file"
              hidden
              multiple
              accept=".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel"
              onChange={handleFileChange}
            />
          </Button>
          {files.length > 0 && (
            <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1.5, width: '100%', maxWidth: 450 }}>
              {files.map((f, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    bgcolor: 'rgba(255, 255, 255, 0.8)',
                    borderRadius: 2,
                    p: 1.5,
                    border: '1px solid #E2E8F0',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600, color: '#334155', display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    📄 {f.name}
                  </Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFile(idx)}
                    sx={{ color: '#EF4444', bgcolor: 'rgba(239, 68, 68, 0.1)', '&:hover': { bgcolor: '#FEE2E2', color: '#DC2626' } }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Button
          variant="contained"
          onClick={handleUpload}
          disabled={files.length === 0}
          sx={{
            mt: 2,
            bgcolor: '#10B981',
            textTransform: 'none',
            borderRadius: 2,
            px: 6,
            py: 1.5,
            fontWeight: 600,
            '&:hover': { bgcolor: '#059669' },
            '&.Mui-disabled': {
              bgcolor: '#A7F3D0',
              color: '#047857'
            }
          }}
        >
          Process & View Data
        </Button>
      </Paper>
    </Box>
  );
};

export default UploadData;
